import React, { useState, useEffect, useRef } from "react";
import { Tabs, Card, Row, Col, Statistic } from "antd";
import {
  FileTextOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import BillForm from "./BillForm";
import BillsList from "./BillsList";
import PatientsList from "./PatientsList";
import { CreateBillRequest } from "../../types/Billing";
import {
  fetchBillStatsStart,
  createBillStart,
  clearError,
} from "../../store/slices/billingSlice";
import { getStockItemsStart } from "../../store/slices/stockSlice";

const Billing: React.FC = () => {
  const dispatch = useDispatch();
  const { billStats, loading, error } = useSelector(
    (state: RootState) => state.billing
  );
  const [activeTab, setActiveTab] = useState("bills");
  const pendingCreateRef = useRef(false);

  useEffect(() => {
    dispatch(fetchBillStatsStart());
  }, [dispatch]);

  useEffect(() => {
    if (!pendingCreateRef.current || loading) return;

    pendingCreateRef.current = false;
    if (error) {
      // Mutation toast already shown by axios interceptor
      dispatch(clearError());
      return;
    }

    setActiveTab("bills");
    dispatch(getStockItemsStart({ page: 1, limit: 500 }));
  }, [loading, error, dispatch]);

  // Clear leftover Redux errors from other billing actions (fetch, etc.)
  useEffect(() => {
    if (error && !pendingCreateRef.current) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleCreateBill = (billData: CreateBillRequest) => {
    pendingCreateRef.current = true;
    dispatch(createBillStart(billData));
  };

  return (
    <div>
      {activeTab !== "create" && (
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col xs={12} sm={6}>
            <Card className="stat-card" size="small">
              <Statistic
                title="Bills"
                value={billStats?.totalBills || 0}
                prefix={<FileTextOutlined />}
                loading={loading}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="stat-card" size="small">
              <Statistic
                title="Revenue"
                value={billStats?.totalRevenue || 0}
                prefix={<DollarOutlined />}
                precision={0}
                loading={loading}
                formatter={(value) => `₹${value}`}
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="stat-card" size="small">
              <Statistic
                title="Pending"
                value={billStats?.pendingAmount || 0}
                prefix={<ClockCircleOutlined />}
                precision={0}
                loading={loading}
                formatter={(value) => `₹${value}`}
                valueStyle={{ fontSize: 18, color: "#ca8a04" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card className="stat-card" size="small">
              <Statistic
                title="Overdue"
                value={billStats?.overdueAmount || 0}
                prefix={<ExclamationCircleOutlined />}
                loading={loading}
                precision={0}
                formatter={(value) => `₹${value}`}
                valueStyle={{ fontSize: 18, color: "#b91c1c" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        type="card"
        items={[
          {
            key: "bills",
            label: (
              <span>
                <FileTextOutlined /> Bills
              </span>
            ),
            children: (
              <BillsList onCreateNew={() => setActiveTab("create")} />
            ),
          },
          {
            key: "create",
            label: (
              <span>
                <PlusOutlined /> New Bill
              </span>
            ),
            children: (
              <BillForm onSubmit={handleCreateBill} loading={loading} />
            ),
          },
          {
            key: "patients",
            label: (
              <span>
                <UserOutlined /> Patients
              </span>
            ),
            children: <PatientsList />,
          },
        ]}
      />
    </div>
  );
};

export default Billing;
